// おまもりガチャ（GAME_DESIGN 18章・19章。ガチャ券1枚で1回。券は遊んで手に入るだけで、課金では手に入らない。霊玉は使わない）。排出率を必ず表示する。
// 毎日1回の無料ガチャ（JST の日付）・その日のボーナス・週替わりのピックアップ・1回／10連（ランクA以上を1回確定）。
// 夜の境内にお守り。引くと演出（ui/omikuji-fx.ts）→ 結果。スクロールしない。ルート名 gacha・内部キー omikuji は互換のため。
import {
  META_CONFIG,
  GACHA,
  OMAMORI,
  RARITY_LABEL,
  RARITIES,
  freeOmikujiAvailable,
  omikujiPerk,
  omikujiPickup,
  omikujiRates,
  omamoriRangeText,
  type GachaItem,
} from '@hyakki/core';
import { api } from '../api';
import { gachaArt, omamoriIconStyle, omikujiArt, OMK_ICON } from '../assets';
import { register } from '../router';
import { act, state, subscribe } from '../state';
import { esc, fmt, resIcon } from '../ui/dom';
import { icon } from '../ui/icons';
import { playOmikuji, type OmkPhase } from '../ui/omikuji-fx';
import { openModal, toast } from '../ui/overlay';
import { errorText } from '../ui/status';

const pct = (r: number) => `${(r * 100).toFixed(r < 0.01 ? 2 : 1)}%`;
const today = () => state.me?.day ?? new Date(Date.now() + 9 * 3600e3).toISOString().slice(0, 10);

function ratesHtml(day: string) {
  const r = omikujiRates(day);
  const g = META_CONFIG.gacha;
  const fort = r.fortunes.map((f) => `<tr><th>${esc(f.label)}</th><td>${pct(f.rate)}</td>${RARITIES.map((x) => `<td>${pct(r.byFortune[f.fortune][x])}</td>`).join('')}</tr>`).join('');
  const top2 = g.tenGuarantee.map((f) => r.fortunes.find((x) => x.fortune === f)!);
  const top2Rate = top2.reduce((a, x) => a + x.rate, 0);
  return `<div class="omk-rates">
    <section><h3 class="sec-title">景品</h3><table class="rates"><tbody>${r.prizes.filter((x) => x.rate > 0).map((x) => `<tr><th>${esc(x.label)}</th><td>${pct(x.rate)}</td></tr>`).join('')}</tbody></table>
      <p class="muted small">1回ごとに抽選ランク（S〜D）を引き、ランクでお守りのレア度の率が決まります。</p></section>
    <section><h3 class="sec-title">抽選ランクとお守りのレア度</h3><table class="rates"><thead><tr><th>ランク</th><th>率</th>${RARITIES.map((x) => `<th>${RARITY_LABEL[x]}</th>`).join('')}</tr></thead><tbody>${fort}
      <tr class="sum"><th>お守り全体</th><td></td>${RARITIES.map((x) => `<td>${pct(r.rarity[x])}</td>`).join('')}</tr></tbody></table>
      <p class="muted small">10連：10回の中にランクS・Aが1つも無いときは、10回目のランクをS・Aの中から抽選し直します（S ${pct(top2[0].rate / top2Rate)}・A ${pct(top2[1].rate / top2Rate)}。景品はそのまま抽選）。極のお守りが出ないまま${r.pity}回目になると、その回は極のお守りが確定します（無料の回も数えます）。</p></section>
    <section><h3 class="sec-title">お守りの主効果${r.pickup ? `<small>今週のピックアップ：${esc(r.pickup.name)}</small>` : ''}</h3><table class="rates"><tbody>${r.omamori
      .map((x) => {
        const eff = [OMAMORI[x.defId]?.rule, omamoriRangeText(x.defId)].filter(Boolean).join('・');
        return `<tr class="${x.pickup ? 'pk' : ''}"><th class="om-rate">${esc(x.name)}<b>：${pct(x.rate)}</b>${eff ? `<small>${esc(eff)}</small>` : ''}</th></tr>`;
      })
      .join('')}</tbody></table></section>
    <p class="muted small">無料の1回のランクで、その日のボーナスが付きます（S：装備のドロップ+${Math.round((g.perks.daikichi?.drop ?? 0) * 100)}%／A：その日のガチャ（券・ショップの10連・月パスの10連）でランクS・Aの抽選の重み×${g.perks.chukichi?.luck ?? 1}／D：翌日の無料の1回でランクS・Aの抽選の重み×${g.kyoNextDayMult}／B・C：なし）。上の率はボーナスの無いときの値です。</p>
    <p class="muted small">ガチャ券は遊んで手に入るものだけで、購入できません。ショップの「おまもりガチャ10連」「はじめての10連」「討伐記念10連」「月パス」は、買ったその場で券の10連と同じ率で引きます（ランクAのボーナスも同じく付きます）。お守りの副効果は並0〜1・良1〜2・優2〜3・極3個。</p></div>`;
}

register('gacha', {
  title: 'おまもりガチャ',
  nav: 'gacha',
  mount(el) {
    let busy = false;
    const draw = () => {
      const p = state.profile;
      const day = today();
      const have = state.resources.kuji ?? 0;
      const free = p ? freeOmikujiAvailable(p, day) : false;
      const perk = p ? omikujiPerk(p, day) : null;
      const pk = omikujiPickup(day);
      const pity = p?.gacha?.pity ?? 0;
      const c1 = GACHA.cost1;
      const c10 = GACHA.cost10;
      const pkRate = omikujiRates(day).omamori.find((x) => x.pickup)?.rate ?? 0;
      const bonus = free
        ? `<b>無料ガチャ</b>${perk?.luckToday ? '<small>ランクS・Aが出やすい</small>' : ''}`
        : `<b>今日のボーナス</b><small>${esc(perk?.text || 'なし')}</small>`;
      el.innerHTML = `<div class="page fill omk-page"><section class="omk-scene">
        <div class="omk-scene-bg"></div>
        <h1 class="omk-title plate">おまもりガチャ</h1>
        <div class="omk-wallet panel-paper"><span class="ri ri-kuji"></span><span>ガチャ券</span><b>${fmt(have)}</b></div>
        <div class="omk-side">
          <div class="omk-today panel-paper"><span class="omk-today-ic" style="${gachaArt.ticket}"></span><span class="omk-today-t">${bonus}</span></div>
          <div class="omk-pickup panel-paper"><span class="omk-pk-ic" style="${omikujiArt.icon(OMK_ICON.pickup)}"></span><span class="om-art" style="${omamoriIconStyle(pk.defId)}"></span>
            <span class="omk-pk-t"><b>今週のピックアップ</b><span>${esc(pk.name)}<small>出現率 ${pct(pkRate)}</small></span><small>${esc(pk.until.slice(5).replace('-', '/'))}まで</small></span></div>
        </div>
        <button class="omk-shrine" data-n="${free ? 'free' : '1'}" aria-label="おまもりガチャを引く" ${busy ? 'disabled' : ''}>
          <span class="omk-shrine-glow"></span><span class="omk-shrine-box" style="${gachaArt.omamori}"></span>
        </button>
        <div class="omk-btns">
          ${
            free
              ? `<button class="btn gold big omk-free" data-n="free" title="1日1回・券は使いません"><span>無料で1回</span><small>1日1回</small></button>`
              : `<p class="omk-free-done">${icon('check')}今日の無料は引きました<small>明日 0時（日本時間）にまた引けます</small></p>`
          }
          <div class="omk-pay">
            <button class="btn primary big" data-n="1" ${have >= c1 ? '' : 'disabled'}><span>1回引く</span><small>${resIcon('kuji')}${fmt(c1)}枚</small></button>
            <button class="btn primary big ten" data-n="10" ${have >= c10 ? '' : 'disabled'}><span>10連</span><small>${resIcon('kuji')}${fmt(c10)}枚・ランクA以上1回確定</small></button>
          </div>
          <div class="omk-sub"><button class="btn paper-btn small" data-rates>排出率${icon('right', 'chev')}</button><span class="omk-pity">極まであと<b>${Math.max(1, GACHA.pity - pity)}</b>回</span></div>
        </div>
      </section></div>`;
    };
    draw();
    const off = subscribe((ch) => (ch.has('resources') || ch.has('profile') || ch.has('me')) && !busy && draw());

    const pull = async (n: 'free' | '1' | '10') => {
      if (busy) return;
      const cost = n === '10' ? GACHA.cost10 : n === '1' ? GACHA.cost1 : 0;
      if ((state.resources.kuji ?? 0) < cost) return toast('ガチャ券が足りません', 'error');
      busy = true;
      el.querySelectorAll<HTMLButtonElement>('[data-n]').forEach((x) => (x.disabled = true));
      try {
        const r = n === 'free' ? await act(api.gachaFree()) : await act(api.gacha(n === '10' ? 10 : 1));
        const items = ((r as { items?: GachaItem[] }).items ?? []) as GachaItem[];
        if (items.length) {
          const againN = n === '10' ? '10' : '1';
          const againCost = againN === '10' ? GACHA.cost10 : GACHA.cost1;
          await playOmikuji(items, {
            free: n === 'free',
            zeni: items.reduce((t, x) => t + (x.kind === 'omamori' ? (x.zeni ?? 0) : 0), 0),
            bonus: n === 'free' ? ((r as { perk?: { text?: string } }).perk?.text ?? '') : '',
            again: { label: againN === '10' ? 'もう一度10連' : 'もう1回', disabled: (state.resources.kuji ?? 0) < againCost, run: () => void pull(againN) },
          });
        }
      } catch (err) {
        toast((err as { code?: string }).code === 'already' ? '今日の無料ガチャはもう引きました' : errorText(err), 'error');
      } finally {
        busy = false;
        if (el.isConnected) draw();
      }
    };

    const click = (e: Event) => {
      const t = e.target as HTMLElement;
      if (t.closest('[data-rates]')) return void openModal(ratesHtml(today()), { title: '排出率・内容', wide: true });
      const b = t.closest<HTMLButtonElement>('[data-n]');
      if (b && !b.disabled) void pull(b.dataset.n as 'free' | '1' | '10');
    };
    el.addEventListener('click', click);
    // 撮影・確認用（開発時だけ）：見本の結果で演出を再生し、指定の段で止める
    if (import.meta.env.DEV) (window as unknown as { __gachaDemo?: unknown }).__gachaDemo = demo;
    return () => {
      off();
      el.removeEventListener('click', click);
    };
  },
});

/** 見本の結果（演出の確認用） */
function demo(count: 1 | 10, holdAt?: OmkPhase, top: 'ssr' | 'sr' | 'r' | 'n' = 'ssr') {
  const ids = Object.keys(OMAMORI).filter((k) => !OMAMORI[k].sameAs);
  const mk = (i: number, rarity: 'n' | 'r' | 'sr' | 'ssr', f: GachaItem['fortune'] = 'kichi'): GachaItem =>
    i % 4 === 3
      ? { kind: 'zeni', fortune: f, amount: 2000 }
      : { kind: 'omamori', fortune: f, defId: ids[i % ids.length], omamori: { defId: ids[i % ids.length], rarity, q: 0.4 + (i % 5) * 0.12, subs: rarity === 'n' ? [] : [{ id: 'atk', lv: 2 }, { id: 'hp', lv: 3 }, { id: 'surge', lv: 1 }].slice(0, { r: 1, sr: 2, ssr: 3 }[rarity]) as never } };
  const rs = ['n', 'r', 'r', 'n', 'sr', 'n', 'r', 'n', 'r'] as const;
  const items = count === 1 ? [mk(5, top)] : [...rs.map((r, i) => mk(i, r)), mk(9, top)];
  document.querySelectorAll('.omk-fx').forEach((x) => x.remove());
  void playOmikuji(items, { holdAt });
  return items.length;
}
